import { GuideIdentityStatus } from '@/types';
import type { GuideCardData, GuideCardVerification } from '../types';
import { buildGuideProfileUrl, buildIdentityUrl } from './identityUrl';

interface CardIdentitySource {
  guideId: string;
  code: string;
  status: GuideIdentityStatus;
}

interface CardGuideSource {
  name: string;
  photo?: string;
  /** Curated "City, State" (published card). Falls back to city/state. */
  location?: string;
  city?: string;
  state?: string;
  destinations?: string[];
  languages?: string[];
}

export function toCardVerification(status: GuideIdentityStatus): GuideCardVerification {
  if (status === GuideIdentityStatus.ACTIVE) return 'verified';
  if (status === GuideIdentityStatus.REVOKED) return 'revoked';
  return 'suspended';
}

function cleanList(values?: string[]): string[] {
  return (values ?? []).map((value) => value.trim()).filter(Boolean);
}

/**
 * Builds card data from the public payload, the guide's own payload or an
 * admin draft. Text is rendered exactly as curated by the server/admin.
 * Returns null when the backend withheld guide details (non-active or
 * unpublished identities), so callers never render a card with invented data.
 */
export function toGuideCardData(
  identity: CardIdentitySource,
  guide: CardGuideSource | null | undefined,
  /** Guide profile id (GuideProfile._id) — the QR code opens /guides/<profileId>. */
  profileId?: string | null
): GuideCardData | null {
  if (!guide) return null;
  const location =
    guide.location?.trim() || [guide.city, guide.state].map((part) => part?.trim()).filter(Boolean).join(', ');
  return {
    code: identity.code,
    guideId: identity.guideId,
    name: guide.name.trim() || 'Trplin Guide',
    photo: guide.photo?.trim() || undefined,
    location: location || undefined,
    languages: cleanList(guide.languages),
    destinations: cleanList(guide.destinations),
    verification: toCardVerification(identity.status),
    identityUrl: buildIdentityUrl(identity.code),
    profileUrl: profileId ? buildGuideProfileUrl(profileId) : undefined,
  };
}

/** Extracts the profile id from a server `profileUrl` such as "/guides/abc123". */
export function profileIdFromUrl(profileUrl?: string | null): string | null {
  const match = profileUrl?.match(/\/guides\/([^/?#]+)/);
  return match ? match[1] : null;
}

/** "A · B · C +2" — keeps card rows to a single line. */
export function summarizeList(values: string[], max = 3): string {
  const shown = values.slice(0, max).join(' · ');
  const rest = values.length - max;
  return rest > 0 ? `${shown} +${rest}` : shown;
}

export function guideInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.trim().slice(0, 2).toUpperCase() || 'TG';
}

export function cardFileName(code: string, extension: 'pdf' | 'png'): string {
  const safe = code.replace(/[^A-Za-z0-9-]/g, '') || 'card';
  return `trplin-guide-${safe}.${extension}`;
}
